import React, { useCallback } from "react";

import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import { IconButton, Stack, Typography } from "@mui/material";
import { VisibilityLock, WorkspaceFolderCloseIcon } from "assets/material-custom-icons";
import { FolderActions } from "components/Deal-workspace/Menus/FolderActions";
import { useWorkspaceFolderHook } from "../hooks/use-workspace-folder-hook";
import { useNavigate } from "hooks";
import { IFolder } from "store/types";

import { useWorkspace } from "../WorkspaceContext";

export const ROW_HEIGHT = 36;

/** Left offset for the first level of folders under a deal. */
const DEAL_FOLDER_OFFSET_PX = 16;
const INDENT_PX = 25;
const chevronColor = "#0A3142";
const iconColor = "#0A3142";
const labelColor = "#20293C";

interface Props {
  folder: IFolder;
  depth: number;
  /** Full folder list for this deal (used to determine hasChildren). */
  folders: IFolder[];
  isOpen: boolean;
  isSelected: boolean;
  toggleFolder: (id: number) => void;
}

function getChildFolders(parentId: number, folders: IFolder[]): IFolder[] {
  return folders.filter((f) => f.parent_id === parentId);
}

export const WorkspaceFolderRow: React.FC<Props> = React.memo(
  ({ folder, depth, folders, isOpen, isSelected, toggleFolder }) => {
    const navigate = useNavigate();
    const { refetchFolderMetadata, reloadDealFolders, currentDealId } = useWorkspace();

    const refetchHandler = useCallback(async () => {
      // If the folder belongs to a different deal than the current URL, the shared
      // refetchFolders only reloads the current deal — explicitly reload the affected deal too.
      if (folder.deal_id != null && folder.deal_id !== currentDealId) {
        await reloadDealFolders(folder.deal_id);
      } else {
        await refetchFolderMetadata();
      }
    }, [folder.deal_id, currentDealId, reloadDealFolders, refetchFolderMetadata]);

    const childFolders = getChildFolders(folder.id, folders);
    const hasChildren = childFolders.length > 0;

    const navigateToFolder = () => {
      if (folder.deal_id) {
        navigate({ route: "workspaceDealFolder", params: { deal_id: folder.deal_id, folder_id: folder.id } });
      } else {
        navigate({ route: "workspaceFolder", params: { folder_id: folder.id } });
      }
    };

    return (
      <Stack
        direction="row"
        alignItems="center"
        gap="5px"
        sx={{
          width: "100%",
          height: ROW_HEIGHT,
          padding: "0 5px",
          borderRadius: "20px",
          paddingLeft: depth ? `${DEAL_FOLDER_OFFSET_PX + depth * INDENT_PX + 10}px` : `${DEAL_FOLDER_OFFSET_PX}px`,
          backgroundColor: isSelected ? "#DBE8F5" : "transparent",
          "&:hover": {
            backgroundColor: "#ECF1F8"
          },
          "& .folder-menu-trigger": {
            opacity: 0
          },
          "&:hover .folder-menu-trigger": {
            opacity: 0.7
          },
          "& .folder-menu-trigger:hover": {
            opacity: 1
          }
        }}
      >
        {hasChildren ? (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              toggleFolder(folder.id);
            }}
            sx={{ cursor: "pointer" }}
          >
            <ExpandMoreOutlinedIcon
              sx={{
                fill: chevronColor,
                fontSize: 20,
                transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
                transition: "transform 0.2s ease"
              }}
            />
          </IconButton>
        ) : (
          <Stack sx={{ padding: "16px" }} />
        )}
        <Stack
          onClick={navigateToFolder}
          sx={{
            flexDirection: "row",
            alignItems: "center",
            gap: "5px",
            flex: 1,
            minWidth: 0,
            cursor: "pointer"
          }}
        >
          <WorkspaceFolderCloseIcon sx={{ fill: iconColor, fontSize: 20 }} />
          <Typography
            noWrap
            sx={{
              color: labelColor,
              fontWeight: 400,
              fontSize: "16px"
            }}
          >
            {folder.title}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" sx={{ minWidth: folder.is_private ? 53 : 32 }}>
          {folder.is_private && <VisibilityLock sx={{ fill: "#BAC7CE" }} />}
          <FolderActions folder={folder} refetchHandler={refetchHandler} useHook={useWorkspaceFolderHook} />
        </Stack>
      </Stack>
    );
  }
);
